import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react'
import { clamp, drawMap, fmt, P, plans, type Plan } from '../../lib/geo'
import { FILTERS, PLAN_ICON, type Filter } from '../../lib/content'
import { ActivityIcon, Glyph } from '../ui/Icon'
import { safeDpr } from '../../lib/canvas'
import { PlanSheet } from './PlanSheet'

const START = P(28.545, 77.19)
const T_MIN = 17
const T_MAX = 23

interface View { cx: number; cy: number; s: number }

function isOn(p: Plan, t: number, f: Filter) {
  const onAt = t >= p.start - 0.5 && t < p.start + p.dur
  const fits = f === 'all' || (f === 'wf' ? p.wf : p.cat === f)
  return onAt && fits
}

/** The interactive Delhi NCR map: filters, a time slider, glass pins and the plan sheet. */
export function MapWindow() {
  const boxRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [view, setView] = useState<View>({ cx: START.x, cy: START.y, s: 0 })
  const [filter, setFilter] = useState<Filter>('all')
  const [time, setTime] = useState(19.5)
  const [selected, setSelected] = useState('p2')
  const [requested, setRequested] = useState<Record<string, boolean>>({})
  const drag = useRef<{ x: number; y: number; cx: number; cy: number; id: number } | null>(null)

  // Track the window size; pick a starting zoom the first time it is known.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      setSize({ w: r.width, h: r.height })
      setView((v) => (v.s ? v : { ...v, s: clamp(r.width / 1150, 0.42, 1.4) }))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Redraw the map whenever the view or size changes.
  useEffect(() => {
    const c = canvasRef.current
    const ctx = c?.getContext('2d')
    if (!c || !ctx || !size.w || !view.s) return
    const dpr = safeDpr(size.w, size.h, 2, 5_000_000)
    const raf = requestAnimationFrame(() => {
      c.width = Math.round(size.w * dpr)
      c.height = Math.round(size.h * dpr)
      try {
        drawMap(ctx, { s: view.s, dpr, vw: size.w, vh: size.h, cx: view.cx, cy: view.cy })
      } catch (err) {
        console.error('[mauj] Explore map failed to draw.', err)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [view, size])

  const zoom = useCallback((k: number) => setView((v) => ({ ...v, s: clamp(v.s * k, 0.3, 3) })), [])

  // Drag to pan. On touch screens the map is set to touch-action: pan-y, so a vertical swipe still
  // scrolls the page (the browser cancels our drag) while a sideways swipe moves the map.
  const onDown = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return
    drag.current = { x: e.clientX, y: e.clientY, cx: view.cx, cy: view.cy, id: e.pointerId }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.currentTarget.classList.add('drag')
  }
  const onMove = (e: RPointerEvent<HTMLCanvasElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    setView((v) => ({ ...v, cx: d.cx - (e.clientX - d.x) / v.s, cy: d.cy - (e.clientY - d.y) / v.s }))
  }
  const onUp = (e: RPointerEvent<HTMLCanvasElement>) => {
    if (drag.current?.id !== e.pointerId) return
    drag.current = null
    e.currentTarget.classList.remove('drag')
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const visibleCount = plans.filter((p) => isOn(p, time, filter)).length
  const plan = plans.find((p) => p.id === selected) ?? plans[0]
  const pct = ((time - T_MIN) / (T_MAX - T_MIN)) * 100

  return (
    <div className="window glass" ref={boxRef}>
      <canvas ref={canvasRef} aria-label="Map of Delhi NCR with example plans" role="img" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
      <div className="win-vig" />
      <div>
        {view.s > 0 && plans.map((p) => {
          const on = isOn(p, time, filter)
          const x = size.w / 2 + (p.x - view.cx) * view.s
          const y = size.h / 2 + (p.y - view.cy) * view.s
          const cls = ['mpin', p.wf && 'wfp', p.filled >= p.spots && 'full', !on && 'off', p.id === selected && 'sel'].filter(Boolean).join(' ')
          return (
            <button key={p.id} type="button" className={cls} style={{ left: x, top: y }} tabIndex={on ? 0 : -1} aria-hidden={!on || undefined}
              aria-label={`${p.title}, ${fmt(p.start)}`} aria-pressed={p.id === selected} onClick={() => setSelected(p.id)}>
              <ActivityIcon name={PLAN_ICON[p.id] ?? 'chai'} stroke="#FFF4DC" accent="#F8D373" />
            </button>
          )
        })}
      </div>
      <div className="ov search glass thin"><Glyph name="search" width={2} />Search plans in Delhi NCR<b>{visibleCount} {visibleCount === 1 ? 'plan' : 'plans'} on</b></div>
      <div className="ov filters" role="group" aria-label="Filter plans">
        {FILTERS.map((f) => (
          <button key={f.id} type="button" className="fchip" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</button>
        ))}
      </div>
      <div className="ov zoom glass thin">
        <button type="button" aria-label="Zoom in" onClick={() => zoom(1.35)}>+</button>
        <button type="button" aria-label="Zoom out" onClick={() => zoom(1 / 1.35)}>−</button>
      </div>
      <div className="ov time glass thin">
        <div className="row"><label htmlFor="time-range">Plans on at</label><b>{fmt(time)}</b></div>
        <input id="time-range" type="range" min={T_MIN} max={T_MAX} step={0.5} value={time} style={{ ['--p' as string]: `${pct}%` }} onChange={(e) => setTime(Number(e.target.value))} />
        <div className="ticks" aria-hidden="true"><span>5 PM</span><span>6</span><span>7</span><span>8</span><span>9</span><span>10</span><span>11 PM</span></div>
      </div>
      <PlanSheet plan={plan} requested={!!requested[plan.id]} onToggle={() => setRequested((r) => ({ ...r, [plan.id]: !r[plan.id] }))} />
    </div>
  )
}
